import React, { useEffect, useState } from "react";
import { motion, useScroll, useSpring, AnimatePresence } from "framer-motion";
import { ArrowDownToLine, Volume2, VolumeX } from "lucide-react";
import { useMuted } from "./sound";
import { C, EASE, SECTIONS, RESUME_URL, scrollToId, useContent } from "./theme";

export function SectionHead({ num, eyebrow, heading, italic, aside, light = false, testid }) {
  const ink = light ? C.cream : C.wine;
  return (
    <div data-testid={testid} className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 mb-16 md:mb-24 items-end">
      <div className="md:col-span-8">
        <div className="flex items-center gap-4 mb-8">
          <span className="ed-mono text-xs tracking-[0.25em]" style={{ color: light ? C.goldLight : C.gold }}>{num}</span>
          <span className="h-px w-16" style={{ background: light ? C.goldLight : C.gold }} />
          <span className="ed-mono text-[11px] uppercase tracking-[0.3em]" style={{ color: light ? C.blushDeep : C.muted }}>{eyebrow}</span>
        </div>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1.1, ease: EASE }}
          className="ed-serif text-4xl sm:text-5xl lg:text-7xl font-light leading-[1.02] tracking-[-0.01em]"
          style={{ color: ink }}
        >
          {heading} {italic && <em className="font-normal" style={{ color: light ? C.blushDeep : C.rose }}>{italic}</em>}
        </motion.h2>
      </div>
      {aside && (
        <p className="md:col-span-4 text-sm md:text-base leading-relaxed" style={{ color: light ? `${C.cream}cc` : C.muted }}>
          {aside}
        </p>
      )}
    </div>
  );
}

export function splitHeading(text, fallbackHead, fallbackItalic) {
  if (!text) return [fallbackHead, fallbackItalic];
  const words = text.trim().split(" ");
  if (words.length < 2) return [text, ""];
  const cut = Math.max(1, words.length - 2);
  return [words.slice(0, cut).join(" "), words.slice(cut).join(" ")];
}

export function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30 });
  return <motion.div data-testid="scroll-progress" className="fixed top-0 inset-x-0 h-[2px] z-[70] origin-left" style={{ background: C.gold, scaleX }} />;
}

function RotatingWord({ items }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((x) => (x + 1) % items.length), 2600);
    return () => clearInterval(t);
  }, [items.length]);
  return (
    <span className="block h-3.5 overflow-hidden ed-mono text-[9px] uppercase tracking-[0.28em]" style={{ color: C.muted }}>
      <AnimatePresence mode="wait">
        <motion.span key={i} initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -10, opacity: 0 }} transition={{ duration: 0.5, ease: EASE }} className="block whitespace-nowrap">
          {items[i]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

function useActiveSection() {
  const [active, setActive] = useState(null);
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        const v = entries.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (v.length) setActive(v[0].target.id);
      },
      { threshold: [0.15, 0.4], rootMargin: "-100px 0px -40% 0px" }
    );
    SECTIONS.forEach((s) => { const el = document.getElementById(s.id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, []);
  return active;
}

export function EditorialNav() {
  const nav = useContent("nav");
  const contact = useContent("contact");
  const words = nav.taglineWords?.length ? nav.taglineWords : ["brand strategy", "positioning", "consumer psychology", "storytelling", "product marketing"];
  const active = useActiveSection();
  const [past, setPast] = useState(false);
  useEffect(() => {
    const on = () => {
      const el = document.getElementById("contents");
      setPast(!!el && window.scrollY > el.offsetTop + el.offsetHeight - window.innerHeight - 10);
    };
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  return (
    <header data-testid="clean-navbar" className="fixed top-0 inset-x-0 z-[60] backdrop-blur-xl" style={{ background: `${C.cream}d9`, borderBottom: `1px solid ${C.burgundy}1f` }}>
      <div className="max-w-[1440px] mx-auto px-5 md:px-12 h-16 md:h-[72px] flex items-center justify-between gap-6">
        <a href="/" data-testid="clean-logo" className="flex items-center gap-3 group">
          <span className="w-10 h-10 rounded-full flex items-center justify-center ed-serif italic text-lg transition-transform duration-700 group-hover:rotate-[360deg]" style={{ border: `1px solid ${C.gold}`, color: C.burgundy }}>SA</span>
          <span className="hidden sm:flex flex-col leading-tight min-w-[150px]">
            <span className="ed-serif text-lg md:text-xl" style={{ color: C.wine }}>{nav.logoName || "Shruti Aggarwal"}</span>
            <RotatingWord items={words} />
          </span>
        </a>
        <nav className={`hidden lg:flex items-center gap-1 transition-opacity duration-700 ${past ? "opacity-100" : "opacity-0 pointer-events-none"}`} data-testid="nav-links">
          {SECTIONS.map((s, i) => (
            <button key={s.id} data-testid={`clean-nav-${s.id}`} onClick={() => scrollToId(s.id)} className="relative px-2.5 xl:px-4 py-2 flex items-baseline gap-2 group whitespace-nowrap">
              <span className="ed-mono text-[10px]" style={{ color: C.gold }}>{String(i + 1).padStart(2, "0")}</span>
              <span className="ed-serif text-lg transition-colors duration-300" style={{ color: active === s.id ? C.rose : C.wine }}>{s.label}</span>
              <span className="absolute left-4 right-4 bottom-1 h-px origin-left transition-transform duration-500 scale-x-0 group-hover:scale-x-100" style={{ background: C.rose, transform: active === s.id ? "scaleX(1)" : undefined }} />
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <SoundToggle />
          <button data-testid="nav-contents-btn" onClick={() => scrollToId("contents")} className="lg:hidden ed-mono text-[10px] uppercase tracking-[0.25em] px-3 py-2 rounded-full" style={{ border: `1px solid ${C.burgundy}33`, color: C.wine }}>
            Contents
          </button>
          <a href={contact.resume || RESUME_URL} target="_blank" rel="noreferrer" data-testid="clean-nav-resume" className="inline-flex items-center gap-2 ed-mono text-[10px] md:text-[11px] uppercase tracking-[0.25em] px-4 md:px-5 py-2.5 rounded-full transition-colors duration-500 hover:bg-[#9E3B4B]" style={{ background: C.burgundy, color: C.cream }}>
            Resume <ArrowDownToLine size={13} />
          </a>
        </div>
      </div>
    </header>
  );
}

export function WaxSeal() {
  const m = useContent("marquees");
  const text = (m.spinningBadge || "open to work · brand · strategy · search · product").replace(/ · /g, "  ✦  ");
  return (
    <button data-testid="spinning-badge" onClick={() => scrollToId("contact")} aria-label="Open to work — say hi" className="fixed bottom-6 right-6 z-40 w-[112px] h-[112px] hidden md:flex items-center justify-center group">
      <span className="absolute inset-3 rounded-full flex items-center justify-center ed-serif italic text-2xl transition-transform duration-700 group-hover:scale-110" style={{ background: C.burgundy, color: C.cream, boxShadow: `0 10px 30px ${C.burgundy}40, inset 0 0 0 3px ${C.rich}, inset 0 0 0 4px ${C.gold}66` }}>
        hi.
      </span>
      <svg viewBox="0 0 140 140" className="absolute inset-0 w-full h-full animate-[spin_22s_linear_infinite]">
        <defs><path id="seal-circ" d="M 70,70 m -60,0 a 60,60 0 1,1 120,0 a 60,60 0 1,1 -120,0" /></defs>
        <text className="ed-mono" style={{ fontSize: "8.5px", letterSpacing: "0.22em", fill: C.burgundy, textTransform: "uppercase" }}>
          <textPath href="#seal-circ">{`${text}  ✦  ${text}  ✦`}</textPath>
        </text>
      </svg>
    </button>
  );
}

export function SoundToggle() {
  const [muted, toggle] = useMuted();
  return (
    <button data-testid="sound-toggle" onClick={toggle} aria-label={muted ? "Turn sound on" : "Mute sound"} className="w-9 h-9 md:w-10 md:h-10 rounded-full flex items-center justify-center transition-colors duration-500 hover:bg-[#F2DADE]" style={{ border: `1px solid ${C.burgundy}33`, color: C.burgundy }}>
      {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
    </button>
  );
}

export function EditorialFooter() {
  return (
    <footer data-testid="clean-footer" className="py-10" style={{ background: C.wine, color: C.cream }}>
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 flex flex-col md:flex-row justify-between items-center gap-3" style={{ borderTop: `1px solid ${C.gold}33`, paddingTop: 32 }}>
        <p className="ed-serif text-2xl italic">Shruti Aggarwal</p>
        <p className="ed-mono text-[10px] uppercase tracking-[0.3em]" style={{ color: C.blushDeep }}>© {new Date().getFullYear()} · Printed in New York City</p>
      </div>
    </footer>
  );
}
